import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import ConfirmDialog from '@/components/feature/ConfirmDialog';
import { useToast } from '@/contexts/ToastContext';
import { getAllRoutings, getRoutingById, createRouting, updateRouting } from '@/api/routing.api';
import { getAllItems } from '@/api/item.api';
import { getAllWorkCenters } from '@/api/workcenter.api';
import { getAllMachines } from '@/api/machine.api';
import { stageHourlyCost as hourlyCostOf, stageUnitCost as unitCostOf, money, type StageCostInput } from '../stageCost';
import { getAllInspectionChecklists, type InspectionChecklistResponse } from '@/api/inspectionchecklist.api';
import { getAllQualityParameters, type QualityParameterResponse } from '@/api/qualityparameter.api';
import { useWarehouseStore } from '@/stores/warehouseStore';
import StageMaterialsModal from './StageMaterialsModal';
import { formatWorkCenter } from '@/hooks/useWorkCenters';
import { jobWorkService } from '@/services/jobWorkService';

/* ─── helpers ─── */
function formatMinutes(total: number) {
    const hrs = Math.floor(total / 60);
    const mins = total % 60;
    if (hrs === 0) return `${mins} min`;
    if (mins === 0) return `${hrs} hrs`;
    return `${hrs} hrs ${mins} min`;
}

/* ─── local form types ─── */
interface StageFormRow {
    id: string;
    stageName: string;
    workCenterId: string;
    machineId: string;
    standardTimeMinutes: number | string;
    setupTimeMinutes: number | string;
    qcRequired: boolean;
    qcChecklistId?: string;
    qcChecklistName?: string;
    description: string;
    status?: 'ACTIVE' | 'DRAFT';
    bomComponentIds?: string[];
    /** In-house on a work center, or job work done by a sub-contractor */
    stageType: 'IN_HOUSE' | 'JOB_WORK';
    subcontractorId: string;
    /** Job work charge per unit (₹) */
    jobRate: number | string;
    /** Std time is per unit, or per batch of `batchSize` units (oven: 60 min per 100 pcs) */
    timeBasis: 'PER_UNIT' | 'PER_BATCH';
    batchSize: number | string;
    /** Crew size: people needed on the stage */
    operatorsRequired: number | string;
}

/** The fields of a stage row its cost depends on */
const costInput = (s: StageFormRow): StageCostInput => ({
    machineId: s.machineId,
    operatorsRequired: s.operatorsRequired,
    stageType: s.stageType,
    runMinutes: s.standardTimeMinutes,
    setupMinutes: s.setupTimeMinutes,
    timeBasis: s.timeBasis,
    batchSize: s.batchSize,
    jobRate: s.jobRate,
});

const STAGE_DEFAULTS = {
    stageType: 'IN_HOUSE' as const,
    subcontractorId: '',
    jobRate: '',
    timeBasis: 'PER_UNIT' as const,
    batchSize: '',
    operatorsRequired: 1,
};

interface RoutingFormState {
    name: string;
    code: string;
    itemIds: string[];
    version: string;
    status: 'ACTIVE' | 'DRAFT' | 'OBSOLETE';
}

/* ─── Multi-Select Searchable Item Dropdown ─── */
function MultiItemSearchDropdown({
    value = [],
    onChange,
    items,
}: {
    value: string[];
    onChange: (itemIds: string[]) => void;
    items: any[];
}) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        function onClick(e: MouseEvent) {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        }
        if (open) {
            document.addEventListener('mousedown', onClick);
            return () => document.removeEventListener('mousedown', onClick);
        }
    }, [open]);

    const filtered = useMemo(() => {
        const q = query.toLowerCase().trim();
        if (!q) return items;
        return items.filter(
            (i) =>
                (i.name && i.name.toLowerCase().includes(q)) ||
                (i.code && i.code.toLowerCase().includes(q)) ||
                (i.item_code && i.item_code.toLowerCase().includes(q)) ||
                (i.sku && i.sku.toLowerCase().includes(q))
        );
    }, [query, items]);

    const toggleItem = (itemId: string) => {
        if (value.includes(itemId)) {
            onChange(value.filter((id) => id !== itemId));
        } else {
            onChange([...value, itemId]);
        }
    };

    const removeItem = (itemId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        onChange(value.filter((id) => id !== itemId));
    };

    const isFinishedGood = (i: any) => {
        const rawType = String(i.item_type || i.category || i.type || '').toUpperCase().replace(/[\s-]/g, '_');
        return rawType === 'FINISHED_GOOD';
    };

    const isSemiFinished = (i: any) => {
        const rawType = String(i.item_type || i.category || i.type || '').toUpperCase().replace(/[\s-]/g, '_');
        return rawType === 'SEMI_FINISHED';
    };

    const { finishedGoods, semiFinishedGoods, otherGoods } = useMemo(() => {
        const fg: any[] = [];
        const sfg: any[] = [];
        const other: any[] = [];

        for (const item of filtered) {
            if (isFinishedGood(item)) {
                fg.push(item);
            } else if (isSemiFinished(item)) {
                sfg.push(item);
            } else {
                other.push(item);
            }
        }

        return { finishedGoods: fg, semiFinishedGoods: sfg, otherGoods: other };
    }, [filtered]);

    const selectedItems = useMemo(() => {
        return items.filter((i) => value.includes(i.id));
    }, [value, items]);

    const renderItemRow = (item: any) => {
        const isSelected = value.includes(item.id);
        const isFG = isFinishedGood(item);
        const isSFG = isSemiFinished(item);

        return (
            <div
                key={item.id}
                onClick={() => toggleItem(item.id)}
                className={`px-3 py-2 text-sm hover:bg-indigo-50/50 cursor-pointer flex items-center justify-between transition-colors ${
                    isSelected ? 'bg-indigo-50 font-medium text-indigo-900' : 'text-slate-700'
                }`}
            >
                <div className="flex items-center gap-2 min-w-0">
                    <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {}}
                        className="w-4 h-4 rounded text-indigo-600 border-slate-300 focus:ring-indigo-500 cursor-pointer pointer-events-none"
                    />
                    <div className="truncate">
                        <span className="text-slate-800 font-medium text-xs sm:text-sm">{item.name}</span>
                        {isFG ? (
                            <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium">
                                Finished Good
                            </span>
                        ) : isSFG ? (
                            <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">
                                Semi-Finished
                            </span>
                        ) : item.item_type ? (
                            <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600 font-normal">
                                {item.item_type}
                            </span>
                        ) : null}
                    </div>
                </div>
                <span className="text-xs text-slate-400 font-mono shrink-0 ml-2">
                    {item.code || item.item_code || ''}
                </span>
            </div>
        );
    };

    return (
        <div ref={containerRef} className="relative w-full">
            {/* Box with selected pills + input */}
            <div
                onClick={() => {
                    setOpen(true);
                    inputRef.current?.focus();
                }}
                className={`min-h-[40px] px-2 py-1.5 rounded-lg border bg-white flex flex-wrap items-center gap-1.5 cursor-text transition-all ${
                    open ? 'border-[#4f46e5] ring-2 ring-[#4f46e5]/20' : 'border-[#e2e8f0] hover:border-slate-400'
                }`}
            >
                {selectedItems.map((item) => {
                    const isFG = isFinishedGood(item);
                    return (
                        <span
                            key={item.id}
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-xs font-medium ${
                                isFG
                                    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                                    : 'bg-amber-50 border-amber-200 text-amber-800'
                            }`}
                        >
                            <span className="max-w-[140px] truncate">{item.name}</span>
                            <span
                                className={`text-[9px] px-1 py-0.2 rounded font-bold uppercase ${
                                    isFG ? 'bg-emerald-200/80 text-emerald-800' : 'bg-amber-200/80 text-amber-800'
                                }`}
                            >
                                {isFG ? 'FG' : 'SFG'}
                            </span>
                            <button
                                type="button"
                                onClick={(e) => removeItem(item.id, e)}
                                className="hover:text-red-500 rounded p-0.5 cursor-pointer flex items-center justify-center"
                            >
                                <i className="ri-close-line text-xs" />
                            </button>
                        </span>
                    );
                })}

                <div className="flex-1 min-w-[120px] flex items-center">
                    <input
                        ref={inputRef}
                        type="text"
                        value={query}
                        onChange={(e) => {
                            setQuery(e.target.value);
                            if (!open) setOpen(true);
                        }}
                        onFocus={() => setOpen(true)}
                        placeholder={value.length === 0 ? "Search finished / semi-finished items..." : "Add more items..."}
                        className="w-full text-sm text-[#1e293b] bg-transparent focus:outline-none placeholder:text-[#94a3b8]"
                    />
                </div>

                {value.length > 0 && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            onChange([]);
                        }}
                        className="p-1 rounded text-slate-400 hover:text-slate-600 hover:bg-slate-100 cursor-pointer"
                        title="Clear all"
                    >
                        <i className="ri-close-circle-line text-sm" />
                    </button>
                )}
            </div>

            {/* Dropdown list */}
            {open && (
                <div className="absolute z-30 mt-1 w-full bg-white border border-[#e2e8f0] rounded-xl shadow-xl max-h-72 overflow-y-auto">
                    <div className="p-2 bg-slate-50 flex items-center justify-between text-xs text-slate-500 border-b border-slate-200 sticky top-0 z-20">
                        <span>{filtered.length} items ({finishedGoods.length} Finished, {semiFinishedGoods.length} Semi-Finished)</span>
                        {value.length > 0 && (
                            <span className="font-semibold text-indigo-600">{value.length} selected</span>
                        )}
                    </div>
                    {filtered.length === 0 ? (
                        <div className="px-4 py-3 text-xs text-slate-400 text-center">No matching items found</div>
                    ) : (
                        <div>
                            {/* 1. Finished Goods Section */}
                            {finishedGoods.length > 0 && (
                                <div>
                                    <div className="sticky top-[33px] z-10 px-3 py-1.5 bg-emerald-50/95 backdrop-blur-xs border-y border-emerald-200 flex items-center justify-between text-xs font-semibold text-emerald-800">
                                        <div className="flex items-center gap-1.5">
                                            <i className="ri-checkbox-circle-fill text-emerald-600 text-sm" />
                                            <span>Finished Goods ({finishedGoods.length})</span>
                                        </div>
                                        <span className="text-[10px] font-bold text-emerald-700 bg-emerald-200/80 px-1.5 py-0.5 rounded">
                                            FINISHED GOOD
                                        </span>
                                    </div>
                                    <div className="divide-y divide-slate-100">
                                        {finishedGoods.map(renderItemRow)}
                                    </div>
                                </div>
                            )}

                            {/* 2. Semi-Finished Goods Section */}
                            {semiFinishedGoods.length > 0 && (
                                <div>
                                    <div className="sticky top-[33px] z-10 px-3 py-1.5 bg-amber-50/95 backdrop-blur-xs border-y border-amber-200 flex items-center justify-between text-xs font-semibold text-amber-800">
                                        <div className="flex items-center gap-1.5">
                                            <i className="ri-settings-4-fill text-amber-600 text-sm" />
                                            <span>Semi-Finished Goods ({semiFinishedGoods.length})</span>
                                        </div>
                                        <span className="text-[10px] font-bold text-amber-700 bg-amber-200/80 px-1.5 py-0.5 rounded">
                                            SEMI FINISHED
                                        </span>
                                    </div>
                                    <div className="divide-y divide-slate-100">
                                        {semiFinishedGoods.map(renderItemRow)}
                                    </div>
                                </div>
                            )}

                            {/* 3. Other Goods Section (if any pre-linked items) */}
                            {otherGoods.length > 0 && (
                                <div>
                                    <div className="sticky top-[33px] z-10 px-3 py-1.5 bg-slate-100/95 backdrop-blur-xs border-y border-slate-200 flex items-center justify-between text-xs font-semibold text-slate-700">
                                        <span>Other Linked Items ({otherGoods.length})</span>
                                    </div>
                                    <div className="divide-y divide-slate-100">
                                        {otherGoods.map(renderItemRow)}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/* ─── QC Checklist Dropdown (Opens downwards, only inspection checklists, warehouse-wise) ─── */
function QCChecklistDropdown({
    value,
    onChange,
    checklists,
}: {
    value: string;
    onChange: (checklistId: string, checklistName: string) => void;
    checklists: InspectionChecklistResponse[];
}) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const dropdownRef = useRef<HTMLDivElement>(null);

    const rawSelectedId = value?.replace('checklist:', '') || value;
    const selectedChecklist = checklists.find((c) => c.id === rawSelectedId || `checklist:${c.id}` === value);

    useEffect(() => {
        function handleOutsideClick(e: MouseEvent) {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
                setOpen(false);
            }
        }
        if (open) {
            document.addEventListener('mousedown', handleOutsideClick);
            return () => document.removeEventListener('mousedown', handleOutsideClick);
        }
    }, [open]);

    const filtered = useMemo(() => {
        const q = search.toLowerCase().trim();
        if (!q) return checklists;
        return checklists.filter((c) => c.name.toLowerCase().includes(q));
    }, [search, checklists]);

    return (
        <div ref={dropdownRef} className="relative w-full">
            {/* Trigger Button */}
            <div
                onClick={() => setOpen((prev) => !prev)}
                className={`w-full min-h-[34px] px-2.5 py-1.5 rounded-lg border text-xs bg-white flex items-center justify-between gap-1.5 cursor-pointer transition-all ${
                    open
                        ? 'border-purple-500 ring-2 ring-purple-100 shadow-sm'
                        : 'border-purple-200 hover:border-purple-400 bg-purple-50/30'
                }`}
            >
                {selectedChecklist ? (
                    <div className="flex items-center gap-1.5 min-w-0 flex-1">
                        <i className="ri-checkbox-circle-fill text-purple-600 shrink-0 text-sm" />
                        <span className="font-medium text-slate-800 truncate">{selectedChecklist.name}</span>
                    </div>
                ) : (
                    <span className="text-slate-400 font-normal truncate">
                        — Select Inspection Checklist —
                    </span>
                )}

                <div className="flex items-center gap-1 shrink-0">
                    {selectedChecklist && (
                        <button
                            type="button"
                            onClick={(e) => {
                                e.stopPropagation();
                                onChange('', '');
                                setSearch('');
                            }}
                            className="w-4 h-4 rounded-full hover:bg-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-600 cursor-pointer"
                            title="Clear selection"
                        >
                            <i className="ri-close-line text-xs" />
                        </button>
                    )}
                    <i className={`ri-arrow-down-s-line text-purple-600 text-sm transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
                </div>
            </div>

            {/* Dropdown Menu - ALWAYS OPENS AT THE BOTTOM */}
            {open && (
                <div
                    className="absolute top-full left-0 mt-1 w-full min-w-[260px] bg-white rounded-lg shadow-xl border border-purple-200 z-50 overflow-hidden"
                    style={{ maxHeight: '240px', display: 'flex', flexDirection: 'column' }}
                >
                    {/* Search inside dropdown */}
                    <div className="p-2 border-b border-purple-100 bg-purple-50/50 shrink-0">
                        <div className="relative">
                            <i className="ri-search-line absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-xs" />
                            <input
                                type="text"
                                autoFocus
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search checklist..."
                                className="w-full h-7 pl-7 pr-2 text-xs bg-white border border-purple-200 rounded-md focus:outline-none focus:border-purple-500 focus:ring-1 focus:ring-purple-200"
                            />
                        </div>
                    </div>

                    {/* Checklists options list - ONLY INSPECTION CHECKLIST NAME */}
                    <div className="overflow-y-auto divide-y divide-slate-100 flex-1">
                        {filtered.length === 0 ? (
                            <div className="p-4 text-center text-xs text-slate-400">
                                <i className="ri-inbox-line text-lg mb-1 block text-slate-300" />
                                No inspection checklists found
                            </div>
                        ) : (
                            filtered.map((c) => {
                                const isSelected = selectedChecklist?.id === c.id;
                                return (
                                    <div
                                        key={c.id}
                                        onClick={() => {
                                            onChange(`checklist:${c.id}`, c.name);
                                            setOpen(false);
                                            setSearch('');
                                        }}
                                        className={`px-3 py-2.5 hover:bg-purple-50/60 cursor-pointer transition-colors flex items-center justify-between gap-2 text-xs ${
                                            isSelected ? 'bg-purple-50 font-semibold text-purple-900' : 'text-slate-800'
                                        }`}
                                    >
                                        <div className="flex items-center gap-2 truncate">
                                            <i className={`ri-file-list-3-line text-xs shrink-0 ${isSelected ? 'text-purple-600' : 'text-slate-400'}`} />
                                            <span className="truncate">{c.name}</span>
                                        </div>
                                        {isSelected && <i className="ri-check-line text-purple-600 text-sm shrink-0" />}
                                    </div>
                                );
                            })
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

/* ─── Main Form Page ─── */
export default function RoutingFormPage() {
    const navigate = useNavigate();
    const { id } = useParams<{ id?: string }>();
    const isEdit = !!id;
    const toast = useToast();
    const { selectedWarehouseId } = useWarehouseStore();

    const [activeItems, setActiveItems] = useState<any[]>([]);
    const [activeWorkCenters, setActiveWorkCenters] = useState<any[]>([]);
    // Sub-contractors for job work stages
    const [subcontractors, setSubcontractors] = useState<{ id: string; name: string }[]>([]);
    useEffect(() => {
        jobWorkService
            .getSubContractors()
            .then((list: any[]) => setSubcontractors((list || []).filter((s) => s.is_active !== false).map((s) => ({ id: s.id, name: s.name }))))
            .catch(() => setSubcontractors([]));
    }, []);
    const [activeMachines, setActiveMachines] = useState<any[]>([]);
    // Every active machine, whatever its warehouse: a work center offers the machines linked to it
    const [linkableMachines, setLinkableMachines] = useState<any[]>([]);
    const [allChecklists, setAllChecklists] = useState<InspectionChecklistResponse[]>([]);
    const [qualityParams, setQualityParams] = useState<QualityParameterResponse[]>([]);
    const [allRoutings, setAllRoutings] = useState<any[]>([]);

    const [form, setForm] = useState<RoutingFormState>({
        name: '',
        code: '',
        itemIds: [],
        version: '1.0',
        status: 'DRAFT',
    });

    const [stages, setStages] = useState<StageFormRow[]>([]);
    const [activeStageForMaterials, setActiveStageForMaterials] = useState<number | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [isSaving, setIsSaving] = useState(false);

    /* confirm dialogs */
    const [confirmDeleteStage, setConfirmDeleteStage] = useState<StageFormRow | null>(null);
    const [confirmCancel, setConfirmCancel] = useState(false);

    /* refs for focus management */
    const nameInputRef = useRef<HTMLInputElement>(null);
    const stageNameRefs = useRef<(HTMLInputElement | null)[]>([]);
    const wcSelectRefs = useRef<(HTMLSelectElement | null)[]>([]);
    const stdTimeRefs = useRef<(HTMLInputElement | null)[]>([]);
    const setupTimeRefs = useRef<(HTMLInputElement | null)[]>([]);

    const fetchInitialData = async () => {
        try {
            const [itemsRes, wcRes, mcRes, routingsRes, clRes, qpRes] = await Promise.all([
                getAllItems(),
                getAllWorkCenters(),
                getAllMachines(),
                getAllRoutings(),
                getAllInspectionChecklists().catch(() => ({ success: false, data: [] })),
                getAllQualityParameters().catch(() => ({ success: false, data: [] })),
            ]);

            if (routingsRes.success && routingsRes.data) {
                setAllRoutings(routingsRes.data);
            }
            if (clRes.success && clRes.data) {
                setAllChecklists(clRes.data.filter((c: any) => c.is_active));
            }
            if (qpRes.success && qpRes.data) {
                setQualityParams(qpRes.data.filter((q: any) => q.is_active));
            }

            const currentWh = selectedWarehouseId;

            let itemsLocal: any[] = [];
            if (itemsRes.success && itemsRes.data) {
                // Collect existing linked items in edit mode to preserve them
                let prelinkedIds: string[] = [];
                if (isEdit && id && routingsRes.success && routingsRes.data) {
                    const found = routingsRes.data.find((r: any) => r.id === id);
                    if (found) {
                        prelinkedIds = Array.isArray(found.item_id) && found.item_id.length > 0
                            ? found.item_id
                            : (Array.isArray(found.item_ids) && found.item_ids.length > 0
                                ? found.item_ids
                                : (typeof found.item_id === 'string' ? found.item_id.split(',').map((s: string) => s.trim()).filter(Boolean) : []));
                    }
                }

                itemsLocal = itemsRes.data.filter((i: any) => {
                    const active = i.is_active ?? i.isActive ?? true;
                    if (!active) return false;
                    const rawType = String(i.item_type || i.category || i.type || '').toUpperCase().replace(/[\s-]/g, '_');
                    const isFGorSFG = rawType === 'FINISHED_GOOD' || rawType === 'SEMI_FINISHED';
                    const wasPrelinked = prelinkedIds.includes(i.id);
                    return isFGorSFG || wasPrelinked;
                });

                if (currentWh) {
                    itemsLocal = itemsLocal.filter((i: any) =>
                        (!i.warehouseid && !i.warehouse_id && !i.warehouseId) ? true : (
                            i.warehouseid === currentWh ||
                            i.warehouse_id === currentWh ||
                            i.warehouseId === currentWh ||
                            prelinkedIds.includes(i.id)
                        )
                    );
                }
                setActiveItems(itemsLocal);
            }
            if (wcRes.success && wcRes.data) {
                let wcLocal = wcRes.data.filter((w: any) => w.is_active || w.isActive);
                if (currentWh) {
                    wcLocal = wcLocal.filter((w: any) => w.warehouse_id === currentWh || w.warehouseId === currentWh);
                }
                setActiveWorkCenters(wcLocal);
            }
            if (mcRes.success && mcRes.data) {
                let mcLocal = mcRes.data.filter((m: any) => m.is_active || m.isActive);
                setLinkableMachines(mcLocal);
                if (currentWh) {
                    mcLocal = mcLocal.filter((m: any) => m.warehouse_id === currentWh || m.warehouseId === currentWh);
                }
                setActiveMachines(mcLocal);
            }

            if (isEdit && id && routingsRes.success && routingsRes.data) {
                const found = routingsRes.data.find((r: any) => r.id === id);
                if (found) {
                    const loadedItemIds = Array.isArray(found.item_id) && found.item_id.length > 0
                        ? found.item_id
                        : (Array.isArray(found.item_ids) && found.item_ids.length > 0
                            ? found.item_ids
                            : (typeof found.item_id === 'string' ? found.item_id.split(',').map((s: string) => s.trim()).filter(Boolean) : []));

                    setForm({
                        name: found.name || '',
                        code: found.code || '',
                        itemIds: loadedItemIds,
                        version: found.version || '1.0',
                        status: found.status || 'DRAFT',
                    });

                    const parsedStages = typeof found.stages === 'string' ? JSON.parse(found.stages) : found.stages;
                    const isUuid = (val: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(val || ''));

                    // Saved bom_component_ids go stale whenever the BOM is edited (bom_items are
                    // deleted & re-inserted with new ids). routing_stage_materials keeps the material
                    // item_id, which stays stable — use it so the modal can still tick the right rows.
                    const mappedByStageId = new Map<string, string[]>();
                    const mappedBySeq = new Map<number, string[]>();
                    try {
                        const detailRes = await getRoutingById(id);
                        for (const ds of detailRes?.data?.detailed_stages || []) {
                            const mats = Array.isArray(ds.mapped_materials) ? ds.mapped_materials : [];
                            const matItemIds = Array.from(new Set<string>(
                                mats.map((m: any) => String(m.item_id || m.bom_component_id || '')).filter(Boolean)
                            ));
                            if (matItemIds.length === 0) continue;
                            if (ds.id) mappedByStageId.set(String(ds.id), matItemIds);
                            if (ds.sequence_no != null) mappedBySeq.set(Number(ds.sequence_no), matItemIds);
                        }
                    } catch (e) {
                        console.warn('Could not load stage material mappings:', e);
                    }

                    setStages(
                        (parsedStages || []).map((s: any, sIdx: number) => {
                            const rawCompIds = s.bom_component_ids || s.bomComponentIds || [];
                            const seq = Number(s.sequence_no ?? s.sequence ?? s.stageNumber ?? (sIdx + 1));
                            const dbMapped = mappedByStageId.get(String(s.id)) || mappedBySeq.get(seq);
                            const bCompIds = dbMapped && dbMapped.length > 0
                                ? dbMapped
                                : Array.from(new Set(Array.isArray(rawCompIds) ? rawCompIds : []));
                            const stStatus = s.status || (bCompIds.length > 0 ? 'ACTIVE' : 'DRAFT');
                            return {
                                id: (s.id && isUuid(s.id)) ? s.id : (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => { const r = Math.random()*16|0; return (c==='x'?r:(r&0x3|0x8)).toString(16); })),
                                stageName: s.stageName || s.operationName || '',
                                workCenterId: s.workCenterId || '',
                                machineId: s.machineId || '',
                                standardTimeMinutes: (s.standardTimeMinutes || s.runTimeMinutes) ? (s.standardTimeMinutes || s.runTimeMinutes) : '',
                                setupTimeMinutes: s.setupTimeMinutes ? s.setupTimeMinutes : '',
                                qcRequired: s.qcRequired || false,
                                qcChecklistId: s.qcChecklistId || s.qcChecklist || '',
                                qcChecklistName: s.qcChecklistName || '',
                                description: s.description || '',
                                bomComponentIds: bCompIds,
                                status: stStatus,
                                stageType: String(s.stageType || s.stage_type || '').toUpperCase() === 'JOB_WORK' ? 'JOB_WORK' : 'IN_HOUSE',
                                subcontractorId: s.subcontractorId || s.subcontractor_id || '',
                                jobRate: s.jobRate ?? s.job_rate ?? '',
                                timeBasis: String(s.timeBasis || s.time_basis || '').toUpperCase() === 'PER_BATCH' ? 'PER_BATCH' : 'PER_UNIT',
                                batchSize: s.batchSize ?? s.batch_size ?? '',
                                operatorsRequired: s.operatorsRequired ?? s.operators_required ?? 1,
                            };
                        })
                    );
                } else {
                    toast.error('Routing not found');
                    navigate('/masters/routing');
                }
            }
        } catch (err) {
            toast.error('Failed to load routing data');
        }
    };

    useEffect(() => {
        fetchInitialData();
        setTimeout(() => nameInputRef.current?.focus(), 80);
    }, [id, isEdit, selectedWarehouseId]);

    /* update helper with real-time duplicate check */
    const updateForm = <K extends keyof RoutingFormState>(field: K, value: RoutingFormState[K]) => {
        setForm((p) => ({ ...p, [field]: value }));
        if (field === 'code') {
            const valTrim = typeof value === 'string' ? value.trim().toLowerCase() : '';
            if (valTrim && allRoutings.some((r) => r.code?.trim().toLowerCase() === valTrim && r.id !== id)) {
                setErrors((e) => ({ ...e, code: 'Duplicate routing number. Please choose another number.' }));
                return;
            }
        }
        if (errors[field]) {
            setErrors((e) => {
                const n = { ...e };
                delete n[field];
                return n;
            });
        }
    };

    /* auto-suggest code on name blur */
    const handleNameBlur = async () => {
        if (!isEdit && !form.code.trim() && form.name.trim()) {
            try {
                const rRes = await getAllRoutings();
                const count = (rRes.data || []).length + 1;
                updateForm('code', `RT-${String(count).padStart(3, '0')}`);
            } catch {
                updateForm('code', `RT-${String(Date.now()).slice(-3)}`);
            }
        }
    };

    /* QC parameter helpers */
    const getChecklistParameters = useCallback((checklist: InspectionChecklistResponse) => {
        const pIds = checklist.parameter_ids || [];
        return pIds
            .map((pid) => qualityParams.find((qp) => qp.id === pid))
            .filter(Boolean) as QualityParameterResponse[];
    }, [qualityParams]);

    /* Get checklists strictly for the relevant warehouse (from top-right selectedWarehouseId) */
    const getStageChecklists = useCallback((s: StageFormRow) => {
        // A production stage takes In-Process / Final checklists, never Incoming (goods receipt) ones;
        // the stage's current pick stays listed so an older routing still shows what it has
        const current = String(s.qcChecklistId || '').replace(/^checklist:/, '');
        const forProduction = allChecklists.filter((c) =>
            String(c.applicable_to || '').toUpperCase() !== 'INCOMING' || c.id === current);

        const wc = activeWorkCenters.find((w) => w.id === s.workCenterId);
        const targetWhId = selectedWarehouseId || wc?.warehouse_id;
        if (!targetWhId) return forProduction;

        const whChecklists = forProduction.filter((c) => c.warehouse_id === targetWhId);
        return whChecklists.length > 0
            ? whChecklists
            : forProduction.filter((c) => !c.warehouse_id || c.warehouse_id === targetWhId);
    }, [activeWorkCenters, selectedWarehouseId, allChecklists]);

    const getSelectedQCDetails = useCallback((qcId?: string) => {
        if (!qcId) return null;
        if (qcId.startsWith('checklist:')) {
            const rawId = qcId.replace('checklist:', '');
            const cl = allChecklists.find((c) => c.id === rawId || c.id === qcId);
            if (!cl) return null;
            const params = getChecklistParameters(cl);
            return {
                title: cl.name,
                code: cl.code,
                params: params.map((p) => ({
                    name: p.name,
                    type: p.type,
                    unit: p.unit,
                    min: p.min_value,
                    max: p.max_value,
                })),
            };
        }
        if (qcId.startsWith('param:')) {
            const rawId = qcId.replace('param:', '');
            const qp = qualityParams.find((q) => q.id === rawId || q.id === qcId);
            if (!qp) return null;
            return {
                title: qp.name,
                code: qp.code,
                params: [{
                    name: qp.name,
                    type: qp.type,
                    unit: qp.unit,
                    min: qp.min_value,
                    max: qp.max_value,
                }],
            };
        }
        const cl = allChecklists.find((c) => c.id === qcId);
        if (cl) {
            const params = getChecklistParameters(cl);
            return {
                title: cl.name,
                code: cl.code,
                params: params.map((p) => ({
                    name: p.name,
                    type: p.type,
                    unit: p.unit,
                    min: p.min_value,
                    max: p.max_value,
                })),
            };
        }
        const qp = qualityParams.find((q) => q.id === qcId);
        if (qp) {
            return {
                title: qp.name,
                code: qp.code,
                params: [{
                    name: qp.name,
                    type: qp.type,
                    unit: qp.unit,
                    min: qp.min_value,
                    max: qp.max_value,
                }],
            };
        }
        return null;
    }, [allChecklists, qualityParams, getChecklistParameters]);

    /* stage helpers */
    const addStage = useCallback(() => {
        const newRow: StageFormRow = {
            id: typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => { const r = Math.random()*16|0; return (c==='x'?r:(r&0x3|0x8)).toString(16); }),
            stageName: '',
            workCenterId: '',
            machineId: '',
            standardTimeMinutes: '',
            setupTimeMinutes: '',
            qcRequired: false,
            qcChecklistId: '',
            qcChecklistName: '',
            description: '',
            ...STAGE_DEFAULTS,
        };
        setStages((prev) => [...prev, newRow]);
        setTimeout(() => {
            const idx = stages.length;
            stageNameRefs.current[idx]?.focus();
        }, 60);
    }, [stages.length]);

    const updateStage = (idx: number, patch: Partial<StageFormRow>) => {
        setStages((prev) => {
            const next = [...prev];
            next[idx] = { ...next[idx], ...patch };
            return next;
        });
    };

    const moveStage = (idx: number, direction: -1 | 1) => {
        setStages((prev) => {
            const next = [...prev];
            const swapIdx = idx + direction;
            if (swapIdx < 0 || swapIdx >= next.length) return prev;
            [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
            return next;
        });
    };

    const deleteStage = (stage: StageFormRow) => {
        if (stages.length === 1) {
            setConfirmDeleteStage(stage);
            return;
        }
        setStages((prev) => prev.filter((s) => s.id !== stage.id));
    };

    const confirmDeleteLastStage = () => {
        if (!confirmDeleteStage) return;
        setStages((prev) => prev.filter((s) => s.id !== confirmDeleteStage.id));
        setConfirmDeleteStage(null);
    };

    /* summary */
    const summary = useMemo(() => {
        const totalStages = stages.length;
        const totalStd = stages.reduce((s, r) => s + (Number(r.standardTimeMinutes) || 0), 0);
        const totalSetup = stages.reduce((s, r) => s + (Number(r.setupTimeMinutes) || 0), 0);
        const qcCount = stages.filter((r) => r.qcRequired).length;
        return { totalStages, totalStd, totalSetup, totalTime: totalStd + totalSetup, qcCount };
    }, [stages]);

    /* validation */
    const validate = () => {
        const e: Record<string, string> = {};
        if (!form.name.trim()) e.name = 'Routing name is required';
        if (!form.code.trim()) {
            e.code = 'Code is required';
        } else {
            const isDup = allRoutings.some(
                (r) => r.code?.trim().toLowerCase() === form.code.trim().toLowerCase() && r.id !== id
            );
            if (isDup) {
                e.code = 'Duplicate routing number. Please choose another number.';
            }
        }
        stages.forEach((s, i) => {
            if (!s.stageName.trim()) e[`stage_${i}_name`] = 'Stage name required';
            if (s.stageType === 'JOB_WORK') {
                if (!s.subcontractorId) e[`stage_${i}_sub`] = 'Select the sub-contractor';
            } else {
                // The work center's type decides whether the stage needs a machine
                const wc = activeWorkCenters.find((w) => w.id === s.workCenterId);
                if (wc?.machine_required && !s.machineId) e[`stage_${i}_machine`] = 'Machine required for this work center';
            }
            if (s.timeBasis === 'PER_BATCH' && !(Number(s.batchSize) > 0)) e[`stage_${i}_batch`] = 'Enter units per batch';
            if (!(Number(s.operatorsRequired) >= 1)) e[`stage_${i}_crew`] = 'At least 1 operator';
        });
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    /* save */
    const handleSave = async () => {
        if (!validate()) {
            const isDup = allRoutings.some(
                (r) => r.code?.trim().toLowerCase() === form.code.trim().toLowerCase() && r.id !== id
            );
            if (isDup) {
                toast.error('Duplicate routing number. Please choose another number.');
            } else {
                const noMachine = stages.find((s) => s.stageType !== 'JOB_WORK' &&
                    activeWorkCenters.find((w) => w.id === s.workCenterId)?.machine_required && !s.machineId);
                const noSub = stages.find((s) => s.stageType === 'JOB_WORK' && !s.subcontractorId);
                const noBatch = stages.find((s) => s.timeBasis === 'PER_BATCH' && !(Number(s.batchSize) > 0));
                toast.error(noSub
                    ? `Stage "${noSub.stageName || 'Unnamed'}" is job work — select the sub-contractor`
                    : noBatch
                        ? `Stage "${noBatch.stageName || 'Unnamed'}" is timed per batch — enter units per batch`
                        : noMachine
                            ? `Stage "${noMachine.stageName || 'Unnamed'}" needs a machine — its work center type requires one`
                            : 'Please fix the errors before saving');
            }
            return;
        }
        setIsSaving(true);
        try {
            const isUuid = (val: string) => /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(String(val || ''));
            const genUuid = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => { const r = Math.random()*16|0; return (c==='x'?r:(r&0x3|0x8)).toString(16); }));

            const savedStages = stages.map((s, idx) => {
                const wc = activeWorkCenters.find((w) => w.id === s.workCenterId);
                // A manual work center runs without a machine
                const manual = !!wc && wc.machine_required === false;
                const mc = manual ? undefined : linkableMachines.find((m) => m.id === s.machineId);
                const stageUuid = (s.id && isUuid(s.id)) ? s.id : genUuid();
                return {
                    id: stageUuid,
                    stageNumber: idx + 1,
                    sequence_no: idx + 1,
                    sequence: idx + 1,
                    stageName: s.stageName,
                    workCenterId: s.workCenterId || null,
                    workCenterName: wc?.name ?? null,
                    machineId: manual ? null : s.machineId || null,
                    machineName: mc?.name ?? null,
                    standardTimeMinutes: Number(s.standardTimeMinutes) || 0,
                    setupTimeMinutes: Number(s.setupTimeMinutes) || 0,
                    description: s.description || null,
                    qcRequired: s.qcRequired,
                    qcChecklistId: s.qcChecklistId || null,
                    qcChecklistName: s.qcChecklistName || null,
                };
            });

            const totalTime = savedStages.reduce(
                (sum, s) => sum + s.standardTimeMinutes + s.setupTimeMinutes,
                0,
            );

            const selectedItems = activeItems.filter((i) => form.itemIds.includes(i.id));
            const itemNames = Array.from(new Set(selectedItems.map((i) => i.name)));
            const applicableItems = selectedItems.map((i) => ({ id: i.id, name: i.name, code: i.code }));
            const uniqueItemIds = Array.from(new Set(form.itemIds));

            // Check if ANY stage has not mapped the BOM
            const anyStageUnmapped = stages.some(
                (s) => !s.bomComponentIds || s.bomComponentIds.length === 0
            );
            const finalStatus = anyStageUnmapped ? 'DRAFT' : form.status;

            const payload = {
                name: form.name.trim(),
                code: form.code.trim(),
                itemIds: uniqueItemIds,
                itemNames: itemNames,
                applicableItems: applicableItems,
                itemId: uniqueItemIds.length > 0 ? uniqueItemIds.join(', ') : null,
                itemName: itemNames.length > 0 ? itemNames.join(', ') : null,
                version: form.version.trim() || '1.0',
                status: finalStatus,
                stages: savedStages.map((s, idx) => {
                    const comps = Array.from(new Set(stages[idx]?.bomComponentIds || []));
                    const isMapped = comps.length > 0;
                    return {
                        id: s.id,
                        sequence: s.stageNumber,
                        sequence_no: s.stageNumber,
                        stageNumber: s.stageNumber,
                        workCenterId: s.workCenterId || '',
                        workCenterName: s.workCenterName || '',
                        machineId: s.machineId || '',
                        machineName: s.machineName || '',
                        operationName: s.stageName,
                        stage_name: s.stageName,
                        setupTimeMinutes: s.setupTimeMinutes,
                        runTimeMinutes: s.standardTimeMinutes,
                        description: s.description || '',
                        qcRequired: s.qcRequired,
                        qc_enabled: s.qcRequired,
                        qcChecklistId: s.qcChecklistId || null,
                        qcChecklistName: s.qcChecklistName || null,
                        status: isMapped ? 'ACTIVE' : 'DRAFT',
                        bom_component_ids: comps,
                        // Time basis, crew and in-house / job work
                        ...(() => {
                            const row = stages[idx];
                            const isJobWork = row?.stageType === 'JOB_WORK';
                            const sub = subcontractors.find((x) => x.id === row?.subcontractorId);
                            return {
                                stageType: isJobWork ? 'JOB_WORK' : 'IN_HOUSE',
                                subcontractorId: isJobWork ? row.subcontractorId || null : null,
                                subcontractorName: isJobWork ? sub?.name || null : null,
                                jobRate: isJobWork ? Number(row.jobRate) || 0 : 0,
                                timeBasis: row?.timeBasis === 'PER_BATCH' ? 'PER_BATCH' : 'PER_UNIT',
                                batchSize: row?.timeBasis === 'PER_BATCH' ? Number(row.batchSize) || 0 : 1,
                                operatorsRequired: Math.max(1, Math.round(Number(row?.operatorsRequired) || 1)),
                                ...(isJobWork ? { workCenterId: '', workCenterName: '', machineId: '', machineName: '' } : {}),
                            };
                        })(),
                    };
                }),
                totalTimeMinutes: totalTime,
                isActive: finalStatus !== 'OBSOLETE',
                warehouseId: selectedWarehouseId || null,
            };

            let res;
            if (isEdit && id) {
                res = await updateRouting(id, payload);
            } else {
                res = await createRouting(payload);
            }

            if (res.success) {
                toast.success(
                    anyStageUnmapped
                        ? (isEdit ? 'Routing updated as DRAFT (stages missing BOM mapping)' : 'Routing saved as DRAFT (stages missing BOM mapping)')
                        : (isEdit ? 'Routing updated' : 'Routing created')
                );
                navigate('/masters/routing');
            } else {
                const msg = res.message || 'Failed to save routing';
                if (msg.toLowerCase().includes('duplicate') || msg.toLowerCase().includes('already exists')) {
                    setErrors((prev) => ({ ...prev, code: 'Duplicate routing number. Please choose another number.' }));
                    toast.error('Duplicate routing number. Please choose another number.');
                } else {
                    toast.error(msg);
                }
            }
        } catch (err: any) {
            const errMsg = err?.response?.data?.message || err?.message || 'Failed to save routing';
            const isDuplicate = errMsg.toLowerCase().includes('duplicate') ||
                                errMsg.toLowerCase().includes('already exists') ||
                                errMsg.toLowerCase().includes('routings_code_key');
            if (isDuplicate) {
                const friendly = 'Duplicate routing number. Please choose another number.';
                setErrors((prev) => ({ ...prev, code: friendly }));
                toast.error(friendly);
            } else {
                toast.error(errMsg);
            }
        } finally {
            setIsSaving(false);
        }
    };

    /* keyboard nav inside table */
    const focusStageName = (idx: number) => stageNameRefs.current[idx]?.focus();
    const focusWorkCenter = (idx: number) => wcSelectRefs.current[idx]?.focus();
    const focusStdTime = (idx: number) => stdTimeRefs.current[idx]?.focus();
    const focusSetupTime = (idx: number) => setupTimeRefs.current[idx]?.focus();

    const onStageNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            focusWorkCenter(idx);
        }
    };

    const onWorkCenterKeyDown = (e: React.KeyboardEvent<HTMLSelectElement>, idx: number) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            focusStdTime(idx);
        }
    };

    const onSetupTimeKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, idx: number) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            if (idx === stages.length - 1) {
                addStage();
            } else {
                focusStageName(idx + 1);
            }
        }
    };

    /* get machines for a work center
       NOTE: Machine API returns snake_case (work_center_id) from the DB */
    const getMachinesForWC = (wcId: string) => {
        if (!wcId) return activeMachines;
        return linkableMachines.filter(
            (m) => (m.work_center_id ?? m.workCenterId) === wcId,
        );
    };

    /* Work center of a stage, with the details the stage row shows */
    const wcOf = (wcId: string) => activeWorkCenters.find((w) => w.id === wcId);

    /** Stage std cost per hour on its work center (see ../stageCost.ts); null until the work center (and a needed machine) is set */
    const stageHourlyCost = (s: StageFormRow): number | null => hourlyCostOf(wcOf(s.workCenterId), costInput(s));

    /** Std cost of one unit's run time on an in-house stage (job work shows its job rate instead) */
    const stageUnitCost = (s: StageFormRow): number | null =>
        s.stageType === 'JOB_WORK' ? null : unitCostOf(wcOf(s.workCenterId), costInput(s));

    const machinesForRow = (wcId: string) => getMachinesForWC(wcId);

    const isDirty =
        form.name.trim() ||
        form.code.trim() ||
        stages.length > 0;

    const handleCancel = () => {
        if (isDirty && !isEdit) {
            setConfirmCancel(true);
            return;
        }
        navigate('/masters/routing');
    };

    return (
        <AppLayout>
            <div className="p-6 space-y-6 w-full">
                {/* Top bar */}
                <div className="flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-3">
                        <button
                            onClick={handleCancel}
                            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-[#e2e8f0] text-[#64748b] transition-colors cursor-pointer"
                        >
                            <i className="ri-arrow-left-line text-base" />
                        </button>
                        <div>
                            <h2 className="text-xl font-bold text-[#1e293b]">
                                {isEdit ? 'Edit Routing' : 'New Routing'}
                            </h2>
                            <p className="text-sm text-[#64748b] mt-0.5">
                                {isEdit
                                    ? 'Update routing stages and production sequence'
                                    : 'Create a production routing with configurable stages'}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleCancel}
                            className="h-9 px-4 rounded-lg border border-[#e2e8f0] text-sm font-medium text-[#64748b] hover:bg-[#f8fafc] transition-colors cursor-pointer whitespace-nowrap"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={isSaving}
                            className="flex items-center gap-2 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 transition-colors cursor-pointer whitespace-nowrap"
                        >
                            {isSaving ? (
                                <>
                                    <i className="ri-loader-4-line animate-spin" /> Saving…
                                </>
                            ) : (
                                <>
                                    <i className="ri-save-line" /> Save Routing
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* ── Header Fields ── */}
                <div className="bg-white border border-[#e2e8f0] rounded-xl p-5 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {/* Routing Name */}
                        <div className="space-y-1.5">
                            <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                                Routing Name <span className="text-red-500">*</span>
                            </label>
                            <input
                                ref={nameInputRef}
                                type="text"
                                value={form.name}
                                onChange={(e) => updateForm('name', e.target.value)}
                                onBlur={handleNameBlur}
                                placeholder="e.g. Industrial Pump Routing"
                                className={`w-full h-10 px-3 rounded-lg border text-sm text-[#1e293b] bg-white focus:outline-none focus:ring-2 transition-colors ${errors.name
                                        ? 'border-red-400 focus:ring-red-200'
                                        : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'
                                    }`}
                            />
                            {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
                        </div>

                        {/* Code */}
                        <div className="space-y-1.5">
                            <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                                Code <span className="text-red-500">*</span>
                                <span className="ml-1 text-[10px] font-normal normal-case text-[#94a3b8]">
                                    (auto-suggest RT-XXX)
                                </span>
                            </label>
                            <input
                                type="text"
                                value={form.code}
                                onChange={(e) => updateForm('code', e.target.value.toUpperCase())}
                                placeholder="RT-001"
                                className={`w-full h-10 px-3 rounded-lg border text-sm font-mono text-[#1e293b] bg-white focus:outline-none focus:ring-2 transition-colors ${errors.code
                                        ? 'border-red-400 focus:ring-red-200'
                                        : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'
                                    }`}
                            />
                            {errors.code && <p className="text-xs text-red-500 font-medium">{errors.code}</p>}
                        </div>

                        {/* Linked Items */}
                        <div className="space-y-1.5 md:col-span-2 lg:col-span-1">
                            <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                                Linked Items
                                <span className="ml-1 text-[10px] font-normal normal-case text-[#94a3b8]">
                                    (Finished Goods & Semi-Finished only)
                                </span>
                            </label>
                            <MultiItemSearchDropdown
                                value={form.itemIds}
                                onChange={(itemIds) => {
                                    updateForm('itemIds', itemIds);
                                }}
                                items={activeItems}
                            />
                        </div>

                        {/* Version */}
                        <div className="space-y-1.5">
                            <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                                Version
                            </label>
                            <input
                                type="text"
                                value={form.version}
                                onChange={(e) => updateForm('version', e.target.value)}
                                placeholder="1.0"
                                className="w-full h-10 px-3 rounded-lg border border-[#e2e8f0] text-sm text-[#1e293b] bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20"
                            />
                        </div>

                        {/* Status */}
                        <div className="space-y-1.5">
                            <label className="text-xs font-semibold text-[#64748b] uppercase tracking-wide">
                                Status <span className="text-red-500">*</span>
                            </label>
                            <div className="flex gap-2">
                                {(['ACTIVE', 'DRAFT', 'OBSOLETE'] as const).map((s) => (
                                    <button
                                        key={s}
                                        type="button"
                                        onClick={() => updateForm('status', s)}
                                        className={`flex-1 h-10 rounded-lg border text-xs font-semibold transition-all cursor-pointer whitespace-nowrap ${form.status === s
                                                ? s === 'ACTIVE'
                                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-300 ring-2 ring-emerald-200'
                                                    : s === 'DRAFT'
                                                        ? 'bg-amber-50 text-amber-700 border-amber-300 ring-2 ring-amber-200'
                                                        : 'bg-slate-100 text-slate-600 border-slate-300 ring-2 ring-slate-200'
                                                : 'bg-white text-[#64748b] border-[#e2e8f0] hover:border-[#4f46e5]/40'
                                            }`}
                                    >
                                        {s}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Stages Section ── */}
                <div className="bg-white border border-[#e2e8f0] rounded-xl overflow-hidden">
                    <div className="px-5 py-4 border-b border-[#e2e8f0] flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-indigo-50">
                                <i className="ri-git-branch-line text-[#4f46e5]" />
                            </div>
                            <div>
                                <h3 className="text-sm font-semibold text-[#1e293b]">Production Stages</h3>
                                <p className="text-xs text-[#64748b]">
                                    Define the sequence of operations for this routing
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={addStage}
                            className="flex items-center gap-1.5 h-8 px-3 rounded-lg bg-[#4f46e5] text-white text-xs font-semibold hover:bg-indigo-700 transition-colors cursor-pointer whitespace-nowrap"
                        >
                            <i className="ri-add-line" /> Add Stage
                        </button>
                    </div>

                    {stages.length === 0 ? (
                        <div className="px-5 py-10 text-center">
                            <i className="ri-git-branch-line text-4xl text-[#e2e8f0] block mb-2" />
                            <p className="text-sm text-[#94a3b8]">No stages added yet</p>
                            <button
                                onClick={addStage}
                                className="mt-3 inline-flex items-center gap-1.5 h-8 px-3 rounded-lg border border-[#e2e8f0] text-xs font-medium text-[#4f46e5] hover:bg-indigo-50 transition-colors cursor-pointer whitespace-nowrap"
                            >
                                <i className="ri-add-line" /> Add First Stage
                            </button>
                        </div>
                    ) : (
                        <div className="overflow-x-auto min-h-[300px] pb-24">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                                        <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">
                                            Stage
                                        </th>
                                        <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">
                                            Stage Name
                                        </th>
                                        <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap" title="In-house on a work center, or job work by a sub-contractor">
                                            Type
                                        </th>
                                        <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">
                                            Work Center / Sub-contractor
                                        </th>
                                        <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">
                                            Machine
                                        </th>
                                        {/* Std Time Header with hover info tooltip */}
                                        <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">
                                            <div className="inline-flex items-center gap-1.5 group relative">
                                                <span>Std Time (min)</span>
                                                <button
                                                    type="button"
                                                    tabIndex={-1}
                                                    className="w-4 h-4 rounded-full flex items-center justify-center text-slate-400 hover:text-indigo-600 transition-colors cursor-help"
                                                    aria-label="What is Standard Time?"
                                                >
                                                    <i className="ri-information-line text-xs" />
                                                </button>
                                                <div className="absolute left-0 top-full mt-1.5 hidden group-hover:block z-50 w-56 p-2.5 bg-slate-900 text-white rounded-lg shadow-xl text-xs normal-case font-normal leading-snug whitespace-normal break-words pointer-events-none">
                                                    <p className="font-semibold text-slate-100 flex items-center gap-1.5 mb-1">
                                                        <i className="ri-time-line text-blue-400" />
                                                        Standard Time
                                                    </p>
                                                    <p className="text-slate-300 text-[11px] leading-relaxed">
                                                        Time required to produce or process 1 unit (in minutes).
                                                    </p>
                                                </div>
                                            </div>
                                        </th>
                                        {/* Setup Time Header with hover info tooltip */}
                                        <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">
                                            <div className="inline-flex items-center gap-1.5 group relative">
                                                <span>Setup (min)</span>
                                                <button
                                                    type="button"
                                                    tabIndex={-1}
                                                    className="w-4 h-4 rounded-full flex items-center justify-center text-slate-400 hover:text-indigo-600 transition-colors cursor-help"
                                                    aria-label="What is Setup Time?"
                                                >
                                                    <i className="ri-information-line text-xs" />
                                                </button>
                                                <div className="absolute left-0 top-full mt-1.5 hidden group-hover:block z-50 w-60 p-2.5 bg-slate-900 text-white rounded-lg shadow-xl text-xs normal-case font-normal leading-snug whitespace-normal break-words pointer-events-none">
                                                    <p className="font-semibold text-slate-100 flex items-center gap-1.5 mb-1">
                                                        <i className="ri-settings-3-line text-amber-400" />
                                                        Setup Time
                                                    </p>
                                                    <p className="text-slate-300 text-[11px] leading-relaxed">
                                                        Preparation time before production starts (e.g. machine setup, die change) in minutes.
                                                    </p>
                                                </div>
                                            </div>
                                        </th>
                                        <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap" title="People needed on this stage — labour cost is time × wage × crew">
                                            Crew
                                        </th>
                                        <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">
                                            QC
                                        </th>
                                        <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">
                                            Materials (BOM)
                                        </th>
                                        <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">
                                            Status
                                        </th>
                                        <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-[#f1f5f9]">
                                    {stages.map((s, idx) => {
                                        const machines = machinesForRow(s.workCenterId);
                                        const isFirst = idx === 0;
                                        const isLast = idx === stages.length - 1;
                                        const hasError = errors[`stage_${idx}_name`];
                                        return (
                                            <tr key={s.id} className="align-top hover:bg-[#f8fafc]/60 transition-colors">
                                                {/* Stage No */}
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <div className="flex items-center h-9">
                                                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#f1f5f9] text-xs font-bold text-[#64748b]">
                                                            {idx + 1}
                                                        </span>
                                                    </div>
                                                </td>

                                                {/* Stage Name */}
                                                <td className="px-4 py-3 whitespace-nowrap min-w-[180px]">
                                                    <input
                                                        ref={(el) => {
                                                            stageNameRefs.current[idx] = el;
                                                        }}
                                                        type="text"
                                                        value={s.stageName}
                                                        onChange={(e) => updateStage(idx, { stageName: e.target.value })}
                                                        onKeyDown={(e) => onStageNameKeyDown(e, idx)}
                                                        placeholder="e.g. Cutting, Assembly, Inspection"
                                                        className={`w-full h-9 px-2.5 rounded-md border text-sm bg-white focus:outline-none focus:ring-2 transition-colors ${hasError
                                                                ? 'border-red-400 focus:ring-red-200'
                                                                : 'border-[#e2e8f0] focus:border-[#4f46e5] focus:ring-[#4f46e5]/20'
                                                            }`}
                                                    />
                                                    {hasError && (
                                                        <p className="text-[11px] text-red-500 mt-0.5">{hasError}</p>
                                                    )}
                                                </td>

                                                {/* Stage type: in-house or job work */}
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <select
                                                        value={s.stageType || 'IN_HOUSE'}
                                                        tabIndex={-1}
                                                        onChange={(e) => {
                                                            const t = e.target.value as StageFormRow['stageType'];
                                                            updateStage(idx, t === 'JOB_WORK'
                                                                ? { stageType: t, workCenterId: '', machineId: '' }
                                                                : { stageType: t, subcontractorId: '', jobRate: '' });
                                                        }}
                                                        className={`w-[110px] h-9 px-2 rounded-md border text-sm font-medium focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20 cursor-pointer ${s.stageType === 'JOB_WORK' ? 'bg-amber-50 border-amber-200 text-amber-800' : 'bg-white border-[#e2e8f0] text-[#1e293b]'}`}
                                                    >
                                                        <option value="IN_HOUSE">In-house</option>
                                                        <option value="JOB_WORK">Job Work</option>
                                                    </select>
                                                </td>

                                                {/* Work center, or sub-contractor + job rate for a job work stage */}
                                                <td className="px-4 py-3 whitespace-nowrap min-w-[210px]">
                                                    {s.stageType === 'JOB_WORK' ? (
                                                        <div className="space-y-1.5">
                                                            <select
                                                                value={s.subcontractorId}
                                                                onChange={(e) => updateStage(idx, { subcontractorId: e.target.value })}
                                                                title={errors[`stage_${idx}_sub`] || 'The plating / painting vendor; a Job Work Order is raised from the production order'}
                                                                className={`w-full h-9 px-2.5 rounded-md border text-sm bg-white focus:outline-none focus:border-[#4f46e5] cursor-pointer ${errors[`stage_${idx}_sub`] ? 'border-red-400' : 'border-[#e2e8f0]'}`}
                                                            >
                                                                <option value="">— Sub-contractor —</option>
                                                                {subcontractors.map((sc) => (
                                                                    <option key={sc.id} value={sc.id}>{sc.name}</option>
                                                                ))}
                                                            </select>
                                                            <div className="flex items-center gap-1.5 text-xs text-[#64748b]">
                                                                <span>₹</span>
                                                                <input
                                                                    type="number"
                                                                    min={0}
                                                                    step="any"
                                                                    placeholder="0"
                                                                    value={s.jobRate}
                                                                    onChange={(e) => updateStage(idx, { jobRate: e.target.value === '' ? '' : Math.max(0, Number(e.target.value) || 0) })}
                                                                    className="w-24 h-8 px-2 rounded-md border border-[#e2e8f0] text-xs bg-white focus:outline-none focus:border-[#4f46e5]"
                                                                />
                                                                <span>job rate / unit</span>
                                                            </div>
                                                        </div>
                                                    ) : (
                                                    <select
                                                        ref={(el) => {
                                                            wcSelectRefs.current[idx] = el;
                                                        }}
                                                        value={s.workCenterId}
                                                        onChange={(e) => {
                                                            const wcId = e.target.value;
                                                            const wc = activeWorkCenters.find((w) => w.id === wcId);
                                                            const linked = wc?.machine_required ? getMachinesForWC(wcId) : [];
                                                            updateStage(idx, {
                                                                workCenterId: wcId,
                                                                // A machine work center with one machine: take it
                                                                machineId: linked.length === 1 ? linked[0].id : '',
                                                                // The stage starts with the work center's crew (persons / operators per machine)
                                                                ...(wc && Number(wc.persons_required) >= 1
                                                                    ? { operatorsRequired: Number(wc.persons_required) }
                                                                    : {}),
                                                            });
                                                        }}
                                                        onKeyDown={(e) => onWorkCenterKeyDown(e, idx)}
                                                        className="w-full h-9 px-2.5 rounded-md border border-[#e2e8f0] text-sm bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20 cursor-pointer"
                                                    >
                                                        <option value="">— Select —</option>
                                                        {activeWorkCenters.map((w) => (
                                                            <option key={w.id} value={w.id}>
                                                                {formatWorkCenter(w.code, w.name)}{w.type_label || w.type ? ` (${String(w.type_label || w.type).split(' — ')[0]})` : ''}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    )}
                                                    {/* Details of the chosen work center */}
                                                    {s.stageType !== 'JOB_WORK' && (() => {
                                                        const wc = wcOf(s.workCenterId);
                                                        if (!wc) return null;
                                                        const plannable = (Number(wc.working_hours_per_day) || 0) * (Number(wc.efficiency_pct) || 0) / 100;
                                                        const shiftNames = (wc.shifts || []).map((sh: any) => sh.name).join(', ');
                                                        const hourly = stageHourlyCost(s);
                                                        return (
                                                            <div className="mt-1.5 w-[210px] whitespace-normal space-y-0.5 text-[11px] leading-snug text-[#64748b]">
                                                                <p className="flex flex-wrap items-center gap-1">
                                                                    <span className="px-1.5 py-px rounded border border-indigo-200 bg-indigo-50 text-[10px] font-semibold text-[#4f46e5]">
                                                                        {String(wc.type_label || wc.type || '').split(' — ')[0]}
                                                                    </span>
                                                                    {wc.cost_center_name && <span title="Cost center">{wc.cost_center_name}</span>}
                                                                </p>
                                                                <p title="Working hours × efficiency, and the shifts it runs">
                                                                    {Math.round(plannable * 100) / 100} h/day{shiftNames ? ` · ${shiftNames}` : ''}
                                                                </p>
                                                                {Number(wc.persons_required) > 0 && (
                                                                    <p title="Standard crew and labour rate per person">
                                                                        {wc.machine_required && String(wc.type).toUpperCase() !== 'BOTH' ? 'Operators/machine' : 'Persons'} {wc.persons_required} × {money(Number(wc.std_labour_rate) || 0)}/hr
                                                                    </p>
                                                                )}
                                                                <p className="font-semibold text-[#1e293b]" title="Standard cost per hour of this stage: crew × labour rate (+ machine operators and machine rate)">
                                                                    {hourly != null
                                                                        ? `Stage std cost ${money(hourly)}/hr`
                                                                        : wc.machine_required
                                                                            ? 'Pick a machine for the stage cost'
                                                                            : `Work center ${money(Number(wc.std_hourly_cost) || 0)}/hr`}
                                                                </p>
                                                            </div>
                                                        );
                                                    })()}
                                                </td>

                                                {/* Machine (none for job work) */}
                                                <td className="px-4 py-3 whitespace-nowrap min-w-[180px]">
                                                    {s.stageType === 'JOB_WORK' ? (
                                                        <span className="inline-flex items-center h-9 text-xs text-[#94a3b8]">Not needed — sub-contractor</span>
                                                    ) : s.workCenterId && activeWorkCenters.find((w) => w.id === s.workCenterId)?.machine_required === false ? (
                                                        <span className="inline-flex items-center gap-1 h-9 text-xs text-[#94a3b8]" title="Manual work center: no machine">
                                                            <i className="ri-team-line" /> Not needed — manual work
                                                        </span>
                                                    ) : (
                                                    <select
                                                        value={s.machineId}
                                                        onChange={(e) => {
                                                            updateStage(idx, { machineId: e.target.value });
                                                            if (errors[`stage_${idx}_machine`]) {
                                                                setErrors((prev) => {
                                                                    const n = { ...prev };
                                                                    delete n[`stage_${idx}_machine`];
                                                                    return n;
                                                                });
                                                            }
                                                        }}
                                                        disabled={!s.workCenterId}
                                                        title={errors[`stage_${idx}_machine`] || undefined}
                                                        className={`w-full h-9 px-2.5 rounded-md border text-sm bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20 cursor-pointer disabled:bg-[#f8fafc] disabled:text-[#94a3b8] ${errors[`stage_${idx}_machine`] ? 'border-red-400' : 'border-[#e2e8f0]'}`}
                                                    >
                                                        <option value="">
                                                            {!s.workCenterId
                                                                ? '— Select Work Center First —'
                                                                : activeWorkCenters.find((w) => w.id === s.workCenterId)?.machine_required
                                                                    ? '— Select Machine (required) —'
                                                                    : '— Select Machine —'}
                                                        </option>
                                                        {machines.map((m) => (
                                                            <option key={m.id} value={m.id}>
                                                                {[m.machine_code, m.name].filter(Boolean).join(' · ')}
                                                                {` · ₹${Number(m.machine_hour_rate) || 0}/hr`}
                                                                {m.status ? ` · ${String(m.status).charAt(0)}${String(m.status).slice(1).toLowerCase()}` : ''}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    )}
                                                    {s.stageType !== 'JOB_WORK' && s.workCenterId && wcOf(s.workCenterId)?.machine_required && machines.length === 0 && (
                                                        <p className="mt-1 w-[180px] whitespace-normal text-[11px] text-red-500">
                                                            No machine linked to this work center — link one in Masters → Work Centers.
                                                        </p>
                                                    )}
                                                </td>

                                                {/* Std Time, per unit or per batch */}
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <input
                                                        ref={(el) => {
                                                            stdTimeRefs.current[idx] = el;
                                                        }}
                                                        type="number"
                                                        min={0}
                                                        placeholder="0"
                                                        title="Standard Time: Expected run time per unit in minutes"
                                                        value={s.standardTimeMinutes}
                                                        onChange={(e) =>
                                                            updateStage(idx, {
                                                                standardTimeMinutes: e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value) || 0),
                                                            })
                                                        }
                                                        className="w-24 h-9 px-2.5 rounded-md border border-[#e2e8f0] text-sm bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20"
                                                    />
                                                    <div className="mt-1.5 flex flex-col gap-1.5 w-24">
                                                        <select
                                                            value={s.timeBasis || 'PER_UNIT'}
                                                            tabIndex={-1}
                                                            onChange={(e) => updateStage(idx, { timeBasis: e.target.value as StageFormRow['timeBasis'] })}
                                                            title="Per unit: time for every piece (CNC). Per batch: time for a whole load (oven: 60 min for 100 pcs)"
                                                            className="w-full h-8 px-2 rounded-md border border-[#e2e8f0] text-xs text-[#475569] bg-[#f8fafc] focus:outline-none focus:border-[#4f46e5] cursor-pointer"
                                                        >
                                                            <option value="PER_UNIT">per unit</option>
                                                            <option value="PER_BATCH">per batch</option>
                                                        </select>
                                                        {stageUnitCost(s) != null && (
                                                            <p className="text-[11px] text-[#64748b]" title="Run time per unit × stage std cost per hour (setup not included)">
                                                                ≈ {money(stageUnitCost(s)!)}/unit
                                                            </p>
                                                        )}
                                                        {s.timeBasis === 'PER_BATCH' && (
                                                            <input
                                                                type="number"
                                                                min={1}
                                                                placeholder="units / batch"
                                                                value={s.batchSize}
                                                                title={errors[`stage_${idx}_batch`] || 'Units per batch'}
                                                                onChange={(e) => updateStage(idx, { batchSize: e.target.value === '' ? '' : Math.max(0, Number(e.target.value) || 0) })}
                                                                className={`w-full h-8 px-2 rounded-md border text-xs bg-white focus:outline-none focus:border-[#4f46e5] ${errors[`stage_${idx}_batch`] ? 'border-red-400' : 'border-[#e2e8f0]'}`}
                                                            />
                                                        )}
                                                    </div>
                                                </td>

                                                {/* Setup Time */}
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <input
                                                        ref={(el) => {
                                                            setupTimeRefs.current[idx] = el;
                                                        }}
                                                        type="number"
                                                        min={0}
                                                        placeholder="0"
                                                        title="Setup Time: Initial changeover and preparation time in minutes"
                                                        value={s.setupTimeMinutes}
                                                        onChange={(e) =>
                                                            updateStage(idx, {
                                                                setupTimeMinutes: e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value) || 0),
                                                            })
                                                        }
                                                        onKeyDown={(e) => onSetupTimeKeyDown(e, idx)}
                                                        className="w-24 h-9 px-2.5 rounded-md border border-[#e2e8f0] text-sm bg-white focus:outline-none focus:border-[#4f46e5] focus:ring-2 focus:ring-[#4f46e5]/20"
                                                    />
                                                </td>

                                                {/* Crew: operators needed (labour = time × wage × crew) */}
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    {s.stageType === 'JOB_WORK' ? (
                                                        <span className="inline-flex items-center h-9 text-xs text-[#94a3b8]">—</span>
                                                    ) : (
                                                        <input
                                                            type="number"
                                                            min={1}
                                                            step={1}
                                                            value={s.operatorsRequired ?? 1}
                                                            title={errors[`stage_${idx}_crew`] || 'People needed on this stage'}
                                                            onChange={(e) => updateStage(idx, { operatorsRequired: e.target.value === '' ? '' : Math.max(1, parseInt(e.target.value) || 1) })}
                                                            className={`w-16 h-9 px-2.5 rounded-md border text-sm bg-white focus:outline-none focus:border-[#4f46e5] ${errors[`stage_${idx}_crew`] ? 'border-red-400' : 'border-[#e2e8f0]'}`}
                                                        />
                                                    )}
                                                </td>

                                                {/* QC */}
                                                <td className="px-4 py-3 min-w-[260px]">
                                                    <div className="space-y-2">
                                                        <div className="flex items-center gap-2 min-h-9">
                                                            <button
                                                                type="button"
                                                                role="switch"
                                                                aria-checked={s.qcRequired}
                                                                onClick={() =>
                                                                    updateStage(idx, {
                                                                        qcRequired: !s.qcRequired,
                                                                        qcChecklistId: !s.qcRequired ? s.qcChecklistId : '',
                                                                        qcChecklistName: !s.qcRequired ? s.qcChecklistName : '',
                                                                    })
                                                                }
                                                                className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-[#4f46e5]/30 ${
                                                                    s.qcRequired ? 'bg-[#4f46e5]' : 'bg-slate-200'
                                                                }`}
                                                            >
                                                                <span
                                                                    aria-hidden="true"
                                                                    className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-md ring-0 transition duration-200 ease-in-out ${
                                                                        s.qcRequired ? 'translate-x-5' : 'translate-x-0'
                                                                    }`}
                                                                />
                                                            </button>
                                                            <span className={`text-xs font-semibold ${s.qcRequired ? 'text-purple-700' : 'text-slate-400'}`}>
                                                                {s.qcRequired ? 'QC Required' : 'No QC'}
                                                            </span>
                                                        </div>

                                                        {s.qcRequired && (
                                                            <div className="space-y-1.5 pt-0.5">
                                                                <QCChecklistDropdown
                                                                    value={s.qcChecklistId || ''}
                                                                    onChange={(checklistId, checklistName) => {
                                                                        updateStage(idx, {
                                                                            qcChecklistId: checklistId,
                                                                            qcChecklistName: checklistName,
                                                                        });
                                                                    }}
                                                                    checklists={getStageChecklists(s)}
                                                                />

                                                                {(() => {
                                                                    const qcDetails = getSelectedQCDetails(s.qcChecklistId);
                                                                    if (!qcDetails) return null;
                                                                    return (
                                                                        <div className="flex flex-wrap gap-1 max-w-[280px]">
                                                                            {qcDetails.params.length > 0 ? (
                                                                                qcDetails.params.map((param, pIdx) => (
                                                                                    <span
                                                                                        key={pIdx}
                                                                                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-purple-100 text-purple-800 border border-purple-200"
                                                                                        title={`Type: ${param.type}${param.unit ? `, Unit: ${param.unit}` : ''}${param.min !== null && param.min !== undefined ? `, Range: ${param.min} - ${param.max}` : ''}`}
                                                                                    >
                                                                                        <i className="ri-checkbox-circle-fill text-[9px] text-purple-600" />
                                                                                        <span>{param.name}</span>
                                                                                        {param.unit && <span className="text-[9px] text-purple-600 font-mono">({param.unit})</span>}
                                                                                        {param.min !== null && param.min !== undefined && (
                                                                                            <span className="text-[9px] text-purple-700 font-mono">[{param.min}-{param.max}]</span>
                                                                                        )}
                                                                                    </span>
                                                                                ))
                                                                            ) : (
                                                                                <span className="text-[10px] text-slate-400 italic">No parameters linked</span>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })()}
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>

                                                {/* Materials (BOM) */}
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <div className="flex items-center h-9">
                                                    <button
                                                        type="button"
                                                        onClick={() => setActiveStageForMaterials(idx)}
                                                        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-medium border transition-colors cursor-pointer ${
                                                            (s.bomComponentIds && s.bomComponentIds.length > 0)
                                                                ? 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100'
                                                                : 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100'
                                                        }`}
                                                    >
                                                        <i className="ri-list-check-2 text-xs" />
                                                        {(() => {
                                                            const count = s.bomComponentIds ? new Set(s.bomComponentIds).size : 0;
                                                            return count > 0 ? `${count} Linked` : 'Map BOM';
                                                        })()}
                                                    </button>
                                                    </div>
                                                </td>

                                                {/* Stage Status */}
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <div className="flex items-center h-9">
                                                    {(s.bomComponentIds && s.bomComponentIds.length > 0) || s.status === 'ACTIVE' ? (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800">
                                                            <i className="ri-checkbox-circle-fill text-[10px] text-emerald-600" />
                                                            ACTIVE
                                                        </span>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            onClick={() => setActiveStageForMaterials(idx)}
                                                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800 hover:bg-amber-200 cursor-pointer transition-colors"
                                                            title="BOM mapping incomplete — click to map materials"
                                                        >
                                                            <i className="ri-time-line text-[10px] text-amber-600" />
                                                            DRAFT
                                                        </button>
                                                    )}
                                                    </div>
                                                </td>

                                                {/* Actions */}
                                                <td className="px-4 py-3 whitespace-nowrap">
                                                    <div className="flex items-center gap-0.5 h-9">
                                                        <button
                                                            onClick={() => moveStage(idx, -1)}
                                                            disabled={isFirst}
                                                            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-[#f1f5f9] text-[#64748b] disabled:text-[#e2e8f0] disabled:hover:bg-transparent transition-colors cursor-pointer"
                                                            title="Move up"
                                                        >
                                                            <i className="ri-arrow-up-line text-sm" />
                                                        </button>
                                                        <button
                                                            onClick={() => moveStage(idx, 1)}
                                                            disabled={isLast}
                                                            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-[#f1f5f9] text-[#64748b] disabled:text-[#e2e8f0] disabled:hover:bg-transparent transition-colors cursor-pointer"
                                                            title="Move down"
                                                        >
                                                            <i className="ri-arrow-down-line text-sm" />
                                                        </button>
                                                        <button
                                                            onClick={() => deleteStage(s)}
                                                            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-red-50 text-[#64748b] hover:text-red-500 transition-colors cursor-pointer"
                                                            title="Delete stage"
                                                        >
                                                            <i className="ri-delete-bin-line text-sm" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {/* Add stage button below table */}
                    {stages.length > 0 && (
                        <div className="px-5 py-3 border-t border-[#e2e8f0] flex items-center gap-2">
                            <button
                                onClick={addStage}
                                className="flex items-center gap-1.5 h-8 px-3 rounded-lg border border-[#e2e8f0] text-xs font-medium text-[#4f46e5] hover:bg-indigo-50 transition-colors cursor-pointer whitespace-nowrap"
                            >
                                <i className="ri-add-line" /> Add Stage
                            </button>
                            <span className="text-xs text-[#94a3b8]">
                                Press Enter in Setup Time of last row to add a new stage
                            </span>
                        </div>
                    )}
                </div>

                {/* ── Summary Bar ── */}
                <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-xl p-4">
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-white border border-[#e2e8f0]">
                                <i className="ri-stack-line text-[#4f46e5] text-sm" />
                            </div>
                            <div>
                                <p className="text-xs text-[#64748b]">Total Stages</p>
                                <p className="text-lg font-bold text-[#1e293b]">{summary.totalStages}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-white border border-[#e2e8f0]">
                                <i className="ri-time-line text-blue-600 text-sm" />
                            </div>
                            <div>
                                <div className="flex items-center gap-1 group relative">
                                    <p className="text-xs text-[#64748b]">Std Time</p>
                                    <span className="text-slate-400 hover:text-indigo-600 cursor-help" aria-label="Std Time info">
                                        <i className="ri-information-line text-xs" />
                                    </span>
                                    <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50 w-56 p-2 bg-slate-900 text-white rounded-lg shadow-xl text-xs normal-case font-normal leading-snug whitespace-normal break-words pointer-events-none">
                                        <p className="font-semibold text-slate-100 flex items-center gap-1 mb-0.5 text-[11px]">
                                            <i className="ri-time-line text-blue-400" /> Standard Time
                                        </p>
                                        <p className="text-slate-300 text-[10px]">Total run time per unit across all stages.</p>
                                    </div>
                                </div>
                                <p className="text-lg font-bold text-[#1e293b]">{formatMinutes(summary.totalStd)}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-white border border-[#e2e8f0]">
                                <i className="ri-settings-3-line text-amber-600 text-sm" />
                            </div>
                            <div>
                                <div className="flex items-center gap-1 group relative">
                                    <p className="text-xs text-[#64748b]">Setup Time</p>
                                    <span className="text-slate-400 hover:text-indigo-600 cursor-help" aria-label="Setup Time info">
                                        <i className="ri-information-line text-xs" />
                                    </span>
                                    <div className="absolute left-0 bottom-full mb-2 hidden group-hover:block z-50 w-56 p-2 bg-slate-900 text-white rounded-lg shadow-xl text-xs normal-case font-normal leading-snug whitespace-normal break-words pointer-events-none">
                                        <p className="font-semibold text-slate-100 flex items-center gap-1 mb-0.5 text-[11px]">
                                            <i className="ri-settings-3-line text-amber-400" /> Setup Time
                                        </p>
                                        <p className="text-slate-300 text-[10px]">Total preparation time across all stages.</p>
                                    </div>
                                </div>
                                <p className="text-lg font-bold text-[#1e293b]">{formatMinutes(summary.totalSetup)}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-white border border-[#e2e8f0]">
                                <i className="ri-hourglass-line text-emerald-600 text-sm" />
                            </div>
                            <div>
                                <p className="text-xs text-[#64748b]">Total Time</p>
                                <p className="text-lg font-bold text-[#1e293b]">{formatMinutes(summary.totalTime)}</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-white border border-[#e2e8f0]">
                                <i className="ri-shield-check-line text-purple-600 text-sm" />
                            </div>
                            <div>
                                <p className="text-xs text-[#64748b]">QC Stages</p>
                                <p className="text-lg font-bold text-[#1e293b]">{summary.qcCount}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* ── Bottom Save / Cancel ── */}
                <div className="flex items-center justify-end gap-2 pb-4">
                    <button
                        onClick={handleCancel}
                        className="h-9 px-4 rounded-lg border border-[#e2e8f0] text-sm font-medium text-[#64748b] hover:bg-[#f8fafc] transition-colors cursor-pointer whitespace-nowrap"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="flex items-center gap-2 h-9 px-4 rounded-lg bg-[#4f46e5] text-white text-sm font-semibold hover:bg-indigo-700 disabled:opacity-60 transition-colors cursor-pointer whitespace-nowrap"
                    >
                        {isSaving ? (
                            <>
                                <i className="ri-loader-4-line animate-spin" /> Saving…
                            </>
                        ) : (
                            <>
                                <i className="ri-save-line" /> Save Routing
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Confirm delete last stage */}
            <ConfirmDialog
                open={!!confirmDeleteStage}
                title="Delete Last Stage?"
                message="This is the only remaining stage. Deleting it will leave the routing with no stages. Are you sure?"
                variant="warning"
                confirmLabel="Yes, Delete (Y)"
                onConfirm={confirmDeleteLastStage}
                onCancel={() => setConfirmDeleteStage(null)}
            />

            {/* Confirm cancel unsaved */}
            <ConfirmDialog
                open={confirmCancel}
                title="Discard Changes?"
                message="You have unsaved changes. Are you sure you want to leave this page?"
                variant="warning"
                confirmLabel="Yes, Discard (Y)"
                onConfirm={() => {
                    setConfirmCancel(false);
                    navigate('/masters/routing');
                }}
                onCancel={() => setConfirmCancel(false)}
            />

            {/* Stage BOM Materials Modal */}
            {activeStageForMaterials !== null && (
                <StageMaterialsModal
                    isOpen={true}
                    onClose={() => setActiveStageForMaterials(null)}
                    stageNumber={activeStageForMaterials + 1}
                    stageName={stages[activeStageForMaterials]?.stageName || `Stage ${activeStageForMaterials + 1}`}
                    selectedBomComponentIds={stages[activeStageForMaterials]?.bomComponentIds || []}
                    itemIds={form.itemIds}
                    onSave={(selectedIds) => {
                        const newStatus = selectedIds.length > 0 ? 'ACTIVE' : 'DRAFT';
                        updateStage(activeStageForMaterials, {
                            bomComponentIds: selectedIds,
                            status: newStatus,
                        });
                    }}
                />
            )}
        </AppLayout>
    );
}